const completedRequests = new WeakSet();
const failureReasons = {
  wrong_credentials: ['login was rejected because the retailer number, username, or password was incorrect', 'warn'],
  too_many_attempts: ['login was rejected because too many failed login attempts locked the account', 'warn'],
  account_locked: ['login was rejected because the user account is blocked', 'warn'],
  invalid_request: ['login could not start because required authentication details were missing or invalid', 'warn'],
  missing_auth_code: ['the username login response did not include the code needed to finish IAM authentication', 'error'],
  no_matching_user: ['no user with this personal identity number was found for the selected retailer', 'warn'],
  duplicate_users: ['login was rejected because the personal identity number belongs to multiple users at the selected retailer', 'warn'],
  invalid_bankid: ['login was rejected because the BankID is too old or blocked', 'warn'],
  bankid_unresponsive: ['login could not continue because the BankID app did not respond', 'warn'],
  transaction_in_progress: ['BankID login could not start because an identification or signing request is already in progress', 'warn'],
  cancelled: ['the BankID identification was cancelled', 'info'],
  bankid_app_or_qr: ['BankID login could not continue because the app could not be opened or the QR code could not be read', 'warn'],
  unknown_transaction: ['the BankID service could not find the identification request being checked', 'error'],
  bankid_service_error: ['the BankID service reported a technical error and could not complete the request', 'error'],
  network_error: ['the browser could not reach the login service because of a network error', 'error'],
  offline: ['login could not continue because the device has no internet connection', 'warn'],
  missing_bearer: ['the IAM redirect returned without the access token needed to create a SAM session for Ombudsportalen', 'error'],
  upstream_rejected: ['the IAM identity provider rejected the login request', 'warn'],
  session_exchange_failed: ['could not finish setting up the SAM login session needed to use Ombudsportalen after the IAM redirect', 'error'],
  missing_session_cookie: ['SAM returned no login cookie, so the browser cannot use the new SAM session for Ombudsportalen', 'error'],
  session_persistence_failed: ['could not save the login details in the server session before redirecting to IAM', 'error'],
  metadata_missing: ['required retailer profile details were missing; Ombudsportalen will continue with limited functionality', 'warn'],
  metadata_persistence_failed: ['could not save the user and retailer profile details in the session; some Ombudsportalen features may be unavailable', 'error'],
  profile_fetch_failed: ['could not load the user and retailer profile needed to finish setting up Ombudsportalen', 'error'],
  password_change_failed: ['the password service rejected or could not complete the password change', 'error'],
  unexpected_error: ['login failed, but the returned error did not match a known cause; check the stage, code, and HTTP status when available', 'error']
};
const codeReasons = {
  username: {
    2: 'wrong_credentials',
    13: 'wrong_credentials',
    5: 'too_many_attempts',
    6: 'account_locked'
  },
  bankid: {
    16: 'no_matching_user',
    29: 'duplicate_users',
    42: 'unknown_transaction',
    47: 'bankid_service_error',
    45: 'invalid_bankid',
    49: 'bankid_unresponsive',
    50: 'transaction_in_progress',
    51: 'cancelled',
    52: 'cancelled',
    56: 'bankid_app_or_qr'
  }
};
const safeIdentifier = value => {
  if (value !== undefined && value !== null && !['string', 'number'].includes(typeof value)) {
    return 'unknown';
  }
  const text = String(value ?? 'unknown');
  return /^[a-zA-Z0-9_-]{1,64}$/.test(text) ? text : 'unknown';
};
function retailerLogFields(value) {
  if (!['string', 'number'].includes(typeof value)) {
    return {};
  }
  const retailerId = String(value);
  return /^[0-9]{1,64}$/.test(retailerId) ? {
    retailerId
  } : {};
}
function writeIamLog(logger, level, action, message, fields) {
  let label = fields.method;
  if (fields.method === 'bankid' && ['initiate', 'poll'].includes(fields.stage)) {
    label = `bankid_${fields.stage}`;
  }
  const event = {
    category: 'IAM Users',
    action,
    label,
    message: `IAM login: ${message}`,
    ...fields
  };
  const line = Object.entries(event).map(([key, value]) => `${key}=${JSON.stringify(value)}`).join(' ');
  try {
    logger[level](line);
  } catch {
    return false;
  }
  return true;
}
function logIamFailure(logger, details = {}) {
  details = details ?? {};
  const method = ['username', 'bankid'].includes(details.method) ? details.method : 'unknown';
  const reasons = codeReasons[method] || {};
  const code = safeIdentifier(details.code);
  let reason = Object.prototype.hasOwnProperty.call(reasons, code) ? reasons[code] : 'unexpected_error';
  if (details.code === 'missing_auth_code') {
    reason = 'missing_auth_code';
  }
  if (typeof details.reason === 'string' && Object.prototype.hasOwnProperty.call(failureReasons, details.reason)) {
    reason = details.reason;
  }
  const [message, level] = failureReasons[reason];
  const fields = {
    method,
    stage: safeIdentifier(details.stage),
    code,
    reason,
    attemptId: safeIdentifier(details.attemptId),
    ...retailerLogFields(details.retailerId)
  };
  if (details.field) {
    fields.field = safeIdentifier(details.field);
  }
  if (details.requestId) {
    fields.requestId = safeIdentifier(details.requestId);
  }
  for (const field of ['httpStatus', 'durationMs']) {
    if (Number.isFinite(details[field])) {
      fields[field] = details[field];
    }
  }
  for (const field of ['online', 'isThisDevice']) {
    if (typeof details[field] === 'boolean') {
      fields[field] = details[field];
    }
  }
  if (fields.stage === 'session_exchange' && reason === 'session_exchange_failed') {
    if (typeof details.jwtHasUserType === 'boolean') {
      fields.jwtHasUserType = details.jwtHasUserType;
    }
    const types = ['missing', 'unavailable', 'null', 'string', 'number', 'boolean', 'object', 'array', 'undefined'];
    if (types.includes(details.jwtUserTypeType)) {
      fields.jwtUserTypeType = details.jwtUserTypeType;
    }
  }
  let action = 'IAM Login Failure';
  let logMessage = message;
  if (fields.stage === 'metadata') {
    logMessage = `Profile failure: ${message}`;
  } else if (reason === 'cancelled') {
    action = 'IAM Login Cancelled';
  }
  writeIamLog(logger, level, action, logMessage, fields);
  return fields;
}
function logIamCompleted(logger, details, request) {
  details = details ?? {};
  const isRequestObject = request !== null && ['object', 'function'].includes(typeof request);
  if (isRequestObject && completedRequests.has(request)) {
    return;
  }
  const message = details.requiresPasswordChange ? 'IAM authentication completed and the SAM session for Ombudsportalen was created; the user must change their password next' : 'IAM authentication completed and the SAM session for Ombudsportalen was created';
  const written = writeIamLog(logger, 'info', 'IAM Login Success', message, {
    method: ['username', 'bankid'].includes(details.method) ? details.method : 'unknown',
    attemptId: safeIdentifier(details.attemptId),
    requestId: safeIdentifier(details.requestId),
    stage: 'session_exchange',
    requiresPasswordChange: Boolean(details.requiresPasswordChange),
    ...(Number.isFinite(details.durationMs) ? {
      durationMs: details.durationMs
    } : {}),
    ...retailerLogFields(details.retailerId)
  });
  if (isRequestObject && written) {
    completedRequests.add(request);
  }
}
function logIamContext(logger, details) {
  details = details ?? {};
  const messages = {
    request_received: 'received the IAM request to open the username or BankID login page',
    context_resolved: 'checked the saved IAM details needed to return to IAM after username or BankID authentication',
    context_cleared: 'removed saved IAM login details because this request is using the non-IAM login flow',
    entry_redirect: 'sending the browser to the IAM login start page because the details needed to return to IAM are missing'
  };
  if (typeof details.event !== 'string' || !Object.prototype.hasOwnProperty.call(messages, details.event)) {
    return;
  }
  const fields = {
    method: ['username', 'bankid'].includes(details.method) ? details.method : 'unknown',
    attemptId: safeIdentifier(details.attemptId),
    requestId: safeIdentifier(details.requestId),
    stage: safeIdentifier(details.stage),
    ...retailerLogFields(details.retailerId)
  };
  for (const field of ['hasSessionContext', 'hasClientId', 'hasRedirectUri', 'hasState', 'hasNonce']) {
    if (typeof details[field] === 'boolean') {
      fields[field] = details[field];
    }
  }
  if (details.contextSource) {
    fields.contextSource = safeIdentifier(details.contextSource);
  }
  writeIamLog(logger, 'debug', 'IAM Login Debug', messages[details.event], fields);
}
function getIamLogContext(req) {
  req = req ?? {};
  const loginHint = req.session?.iam?.login_hint || req.query?.login_hint;
  let method = 'unknown';
  if (req.session?.iamPreferredLoginPath === '/logga-in/anvandarnamn' || loginHint === 'retailer:password') {
    method = 'username';
  } else if (req.session?.iamPreferredLoginPath === '/logga-in/bank-id' || loginHint === 'retailer:bankid') {
    method = 'bankid';
  }
  const fields = {
    method,
    attemptId: safeIdentifier(req.requestId)
  };
  if (req.requestId) {
    fields.requestId = safeIdentifier(req.requestId);
  }
  const candidates = [req.cookies?.iam_retailer_id, req.session?.iam?.retailer_id, req.session?.loginRememberRetailerId];
  for (const candidate of candidates) {
    const retailerFields = retailerLogFields(candidate);
    if (retailerFields.retailerId) {
      Object.assign(fields, retailerFields);
      break;
    }
  }
  return fields;
}
function protectLogging(operation, fallback) {
  return (...args) => {
    try {
      return operation(...args);
    } catch {
      return fallback ? fallback() : undefined;
    }
  };
}
module.exports = {
  logIamFailure: protectLogging(logIamFailure),
  logIamCompleted: protectLogging(logIamCompleted),
  logIamContext: protectLogging(logIamContext),
  getIamLogContext: protectLogging(getIamLogContext, () => ({
    method: 'unknown',
    attemptId: 'unknown'
  }))
};